import React, { useEffect, useState, useCallback } from "react";
import { getApiCall } from "../../service/httpCall";
import { USER_ROLES } from "../../utilities/constant";
import Loader from "../../components/partials/components/loader";
import { toast } from "react-toastify";
import { InputText } from "primereact/inputtext";

import { API_CERTS_EXPIRY_CHECKER } from "../../service/url";
import { FaSyncAlt } from "react-icons/fa";
import { Storage } from "../../utilities/setting";

const CertExpiryChecker = () => {
  const [certs, setCerts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [globalFilter, setGlobalFilter] = useState("");
  const [loggedInUserRole, setLoggedInUserRole] = useState(null);

  const fetchCertificates = useCallback(async () => {
    setLoading(true);
    try {
      const response = await getApiCall(API_CERTS_EXPIRY_CHECKER);
      setCerts(response?.results || []);
    } catch (err) {
      toast.error("Failed to fetch certificate data");
      setCerts([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const checkUserRole = async () => {
      const rawUser = Storage.getItem("user");
      if (!rawUser) return;
      try {
        const user = JSON.parse(rawUser);
        setLoggedInUserRole(user.role);
      } catch (e) {
        console.error("Invalid user data", e);
      }
    };
    checkUserRole();
  }, []);

  useEffect(() => {
    if (!loggedInUserRole || loggedInUserRole !== USER_ROLES.SUPERADMIN) return;
    fetchCertificates();
  }, [loggedInUserRole, fetchCertificates]);

  const handleRefresh = () => {
    fetchCertificates();
    toast.info("Refreshing certificate data...");
  };

  const filteredCerts = certs
    .filter((cert) =>
      cert.domain?.toLowerCase().includes(globalFilter.toLowerCase())
    )
    .sort((a, b) => (a.daysLeft ?? 9999) - (b.daysLeft ?? 9999));

  if (loggedInUserRole !== USER_ROLES.SUPERADMIN) return null;
  if (loading && certs.length === 0) return <Loader />;

  return (
    <div className="dash-section-card futuristic-monitor" style={{ marginBottom: "30px" }}>
      <div className="dash-section-header">
        <span>SSL Domain Certificate Monitor</span>
        {certs.length > 0 && <span className="dash-count-badge">{certs.length}</span>}
      </div>

      <div className="toolbar-container">
        <div className="search-wrapper">
          <InputText
            value={globalFilter}
            onChange={(e) => setGlobalFilter(e.target.value)}
            placeholder="Search domains..."
            className="dash-search-input"
          />
          <button
            onClick={handleRefresh}
            disabled={loading}
            className="refresh-btn"
            title="Refresh Certificates"
          >
            <FaSyncAlt className={loading ? "rotating" : ""} />
          </button>
        </div>
      </div>

      <div className="ultra-compact-grid">
        {filteredCerts.length > 0 ? (
          filteredCerts.map((cert, index) => {
            const days = cert.daysLeft ?? 0;
            const isExpired = days < 0;
            const percentage = Math.max(0, Math.min(100, Math.round((days / 90) * 100)));

            let ringColor = "#22c55e";
            let statusText = "HEALTHY";
            let statusClass = "healthy";

            if (isExpired) {
              ringColor = "#ef4444";
              statusText = "EXPIRED";
              statusClass = "expired";
            } else if (days <= 7) {
              ringColor = "#ef4444";
              statusText = "CRITICAL";
              statusClass = "expired";
            } else if (days <= 30) {
              ringColor = "#f59e0b";
              statusText = "WARNING";
              statusClass = "warning";
            }

            return (
              <div key={index} className={`tiny-cert-card ${statusClass}`}>
                <div className="tiny-header">
                  <div className="domain-name">{cert.domain}</div>
                  <div className={`expiry-date ${isExpired ? 'pulse' : ''}`}>
                    <span className="exp-label">EXP: </span>
                    {new Date(cert.expiry).toLocaleDateString("en-IN", {
                      day: "numeric",
                      month: "short",
                      year: "numeric"
                    })}
                  </div>
                </div>

                <div className="tiny-gauge-container">
                  <svg className="tiny-gauge" viewBox="0 0 92 92">
                    <circle cx="46" cy="46" r="40" fill="none" stroke="#3a57e8" strokeWidth="6" />
                    <circle
                      cx="46"
                      cy="46"
                      r="40"
                      fill="none"
                      stroke={ringColor}
                      strokeWidth="6"
                      strokeDasharray={`${percentage * 2.513} 251.3`}
                      strokeLinecap="round"
                      transform="rotate(-90 46 46)"
                      style={{ transition: "stroke 0.5s ease" }}
                    />
                    <text x="46" y="47" textAnchor="middle" fontSize="19" fontWeight="700">
                      {isExpired ? "0" : days}
                    </text>
                    <text x="46" y="62" textAnchor="middle" fontSize="8">
                      {isExpired ? "EXPIRED" : "DAYS"}
                    </text>
                  </svg>
                </div>

                <div className="status-tag">
                  <span className={`status-badge ${statusClass}`}>{statusText}</span>
                </div>

                {/* <div className="tiny-last-checked">
                  {cert.lastChecked ? new Date(cert.lastChecked).toLocaleDateString("en-IN", {
                    month: "short", day: "numeric", hour: "2-digit", minute: "2-digit"
                  }) : "-"}
                </div> */}
              </div>
            );
          })
        ) : (
          <div className="no-data">No certificates found</div>
        )}
      </div>

      <style>{`
        .toolbar-container { padding: 0 0px 12px; }
        .search-wrapper { display: flex; align-items: center; gap: 12px; width: 100%; }
        .dash-search-input { flex: 1; }
        
        .refresh-btn {
          border: 1px solid #60a5fa;
          width: 42px;
          height: 42px;
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transition: all 0.2s ease;
        }
        .refresh-btn:disabled { opacity: 0.5; cursor: not-allowed; }

        @keyframes rotate {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        .rotating { animation: rotate 1s linear infinite; }

        /* Pulse Animation for Expired */
        @keyframes pulse {
          0% { opacity: 1; }
          50% { opacity: 0.4; }
          100% { opacity: 1; }
        }

        .pulse {
          animation: pulse 1.8s ease-in-out infinite;
          color: #b10015 !important;
        }

        .ultra-compact-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(158px, 1fr));
          gap: 12px;
          padding: 0 0px 14px;
        }

        .tiny-cert-card {
          border: 1px solid #8a8a8a61;
          border-radius: 16px;
          padding: 12px 10px;
          transition: all 0.3s ease;
        }

        .tiny-cert-card.expired {
          border-color: #ef4444;
          box-shadow: 0 0 15px rgba(239, 68, 68, 0.4);
          animation: borderPulse 2s ease-in-out infinite;
        }

        @keyframes borderPulse {
          0% { box-shadow: 0 0 15px rgba(239, 68, 68, 0.4); }
          50% { box-shadow: 0 0 25px rgba(239, 68, 68, 0.7); }
          100% { box-shadow: 0 0 15px rgba(239, 68, 68, 0.4); }
        }

        .domain-name {
          font-size: 0.82rem;
          font-weight: 600;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        .expiry-date {
          font-size: 0.68rem;
          margin-top: 2px;
        }

        .exp-label { font-weight: 500; }

        .tiny-gauge-container { width: 90px; height: 90px; margin: 8px auto; }
        .tiny-gauge { width: 100%; height: 100%; }

        .status-tag { text-align: center; margin: 6px 0 8px; }
        .status-badge {
          padding: 3px 12px;
          border-radius: 9999px;
          font-size: 0.7rem;
          font-weight: 600;
          text-transform: uppercase;
        }
        .status-badge.healthy { background: #166534; color: #86efac; }
        .status-badge.expired { background: #991b1b; color: #fda4af; }
        .status-badge.warning { background: #92400e; color: #fcd34d; }

        .tiny-last-checked {
          text-align: center;
          font-size: 0.67rem;
        }
      `}</style>
    </div>
  );
};

export default CertExpiryChecker;